import {
    AuthError,
    InteractionRequiredAuthError,
    InteractionType,
    IPublicClientApplication,
    PopupRequest,
    RedirectRequest,
    SilentRequest,
} from "@azure/msal-browser";

import { DEFAULT_INTERACTION_TYPE } from "../azure-auth.const";
import { getAcquireTokenFunctionName } from "../azure-auth.types";

type AcquireAccessTokenOptions = {
    scopes: string[];
    interactionType?: InteractionType;
};

/**
 * Acquires an access token for the specified scopes. If the token is not available in the cache, it will
 * attempt to acquire it using the specified interaction type.
 */
export const acquireAccessToken = async (
    options: AcquireAccessTokenOptions,
    instance: IPublicClientApplication
): Promise<string> => {
    const { scopes, interactionType = DEFAULT_INTERACTION_TYPE } = options;

    const account = instance.getActiveAccount() ?? instance.getAllAccounts().at(0);

    const accessTokenRequest = {
        scopes,
        account,
    } satisfies SilentRequest | PopupRequest | RedirectRequest;

    try {
        const { accessToken } = await instance.acquireTokenSilent(accessTokenRequest);
        return accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            const acquireTokenFunctionName = getAcquireTokenFunctionName(interactionType);
            const data = await instance[acquireTokenFunctionName](accessTokenRequest);
            if (data && data?.accessToken) {
                instance.setActiveAccount(data.account);
                return data.accessToken;
            } else {
                throw new Error(`Token wasn't received from login acquisition request`);
            }
        } else if (error instanceof AuthError) {
            // All msal errors should fall here
            throw new Error(`Something went wrong during the authentication process: ${error.errorMessage}`);
        } else {
            // This should never happen
            throw new Error(`Unexpected error occurred in msal library: ${error}`);
        }
    }
};
